import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Button,
    FieldErrorList,
    FormField,
    Input,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    TextArea
} from 'common/components';
import {
    ACTION_ITEMS,
    CLONE_CONFIRMATION_TITLE,
    CLONE_CONFIRMATION_DESCRIPTION,
    FAILURE_HEADER,
    CLONE_DISABLED,
    CLONE_VALIDATION_ERROR_MESSAGE,
    CLONE_SUB_CATEGORY_TITLE
} from 'common/config';
import {
    ApiStatus,
    ButtonLabel,
    SubCategoryField,
    EntityType,
    InputType,
    MessageType
} from 'common/enums';
import { EntityVM } from 'common/models/data/entity.model';
import { removeValidationError } from 'common/utils/error';
import {
    validateSubCategoryDescription,
    validateSubCategoryForm,
    validateSubCategoryName,
    validateSubCategorySortOrder
} from 'common/utils/validator';
import { RootState } from 'common/redux/core/root.reducer';
import { appStateActions } from 'common/redux/app-state';

import SingleSelectMasterData from 'modules/master-data/components/single-select-master-data/SingleSelectMasterData';
import { masterDataActions } from 'modules/master-data/redux';
import {
    getCloneFeasibility,
    getFieldErrors,
    getHierarchicalDataSelector
} from 'modules/master-data/redux/masterData.selector';

import './SubCategoryClone.scss';

type SubCategoryCloneProps = {
    data: EntityVM;
    onClose: () => void;
    fetchNextHierachySet: () => void;
};
export default function SubCategoryClone({
    data,
    onClose,
    fetchNextHierachySet
}: SubCategoryCloneProps) {
    const [name, setName] = useState<string>(data?.name ? data.name : '');
    const [sortOrder, setSortOrder] = useState<string>(data?.order ? data.order : '');
    const [description, setDescription] = useState<string>(
        data?.description ? data.description : ''
    );
    const [targetModalData, setTargetModalData] = useState<any>({});
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isChooseCloneErrorInfo, setChooseCloneErrorInfo] = useState<boolean>(false);

    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataSelector(state));
    const cloneFeasibility = useSelector((state: RootState) => getCloneFeasibility(state));

    const dispatch = useDispatch();

    useEffect(() => {
        setName(data?.name ? data.name : '');
        setSortOrder(data?.order ? data.order : '');
        setDescription(data?.description ? data.description : '');
    }, [data]);

    useEffect(() => {
        dispatch(
            masterDataActions.getCloneFeasibility({
                id: data.id,
                entityType: data.entityType
            })
        );
    }, [data.entityType, data.id, dispatch]);

    useEffect(() => {
        if (cloneFeasibility.status === ApiStatus.SUCCESS) {
            if (cloneFeasibility.data) {
                setIsModalVisible(true);
            } else {
                dispatch(
                    appStateActions.showMessageModal({
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: CLONE_DISABLED,
                        type: MessageType.Error
                    })
                );
                dispatch(masterDataActions.resetCloneFeasibility());
                onClose();
            }
        } else if (cloneFeasibility.status === ApiStatus.ERROR) {
            dispatch(masterDataActions.resetCloneFeasibility());
            onClose();
        }
    }, [cloneFeasibility, dispatch, onClose]);

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Description);
        errors = validateSubCategoryDescription(event.target.value, errors);
        setDescription(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Name);
        errors = validateSubCategoryName(event.target.value, errors);
        setName(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleSortOrderChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.SortOrder);
        errors = validateSubCategorySortOrder(event.target.value, errors);
        dispatch(masterDataActions.setFieldErrors(errors));
        setSortOrder(event.target.value);
    };

    const handeSave = () => {
        let errors = fieldErrors;
        errors = validateSubCategoryForm(name, sortOrder, errors);
        if (Object.keys(targetModalData).length === 0) {
            setChooseCloneErrorInfo(true);
        } else if (errors.length === 0) {
            setIsConfirmationVisible(true);
            setIsModalVisible(false);
        }
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleRadioButtonChange = (data: any) => {
        dispatch(
            masterDataActions.setRadioButtonSelected({
                targetId: data.id
            })
        );
        setTargetModalData(data);
    };

    const handleExpansionToggle = (rowData: any) => {
        dispatch(masterDataActions.toggleHierarchicalDataExpansion(rowData));
    };

    const handleClose = () => {
        onClose();
        dispatch(masterDataActions.setFieldErrors([]));
    };

    const handleConfirmationClose = () => {
        setIsConfirmationVisible(false);
        handleClose();
    };

    const handleConfirmation = () => {
        const newData = {
            name: name,
            order: sortOrder,
            description: description,
            sourceCategoryId: data.id,
            targetCategoryId: targetModalData?.categoryId,
            targetSubCategoryId:
                targetModalData.entityType === EntityType.SubCategory ? targetModalData.id : null,
            entityType: EntityType.SubCategory
        };
        dispatch(masterDataActions.cloneEntity(newData));
        handleConfirmationClose();
    };

    return (
        <>
            <Modal
                show={isModalVisible}
                onClose={handleClose}
                style={{ width: '77vw' }}
                data-testid="subcategory-clone">
                <ModalHeader>Clone a sub-category</ModalHeader>
                <ModalBody className="sub-category-clone-body">
                    <div className="clone-sub-category-container">
                        <div className="clone-sub-category-container__left-Container">
                            <Label text="Choose where you want to clone the new sub category and fill the form on the right"></Label>
                            <SingleSelectMasterData
                                hideSelectionOnCategory={false}
                                hierarchicalData={hierarchicalData}
                                hideChildIndicatorsOnly={true}
                                preventSelectionOfSubCategoriesHavingIndicators={true}
                                preventSelectionOfSubCategoriesHavingSubCategories={false}
                                updateSelectedRadioButton={handleRadioButtonChange}
                                toggleExpansion={handleExpansionToggle}
                                fetchNextHierachySet={fetchNextHierachySet}
                            />
                        </div>
                        <div className="clone-sub-category-container__right-Container">
                            <Label text="Fill in the below form"></Label>
                            <div className="subcategory-form">
                                <FormField className="subcategory-form-field">
                                    <Label text="Subcategory name"></Label>
                                    <Input
                                        type={InputType.TEXT}
                                        value={name}
                                        onChange={handleNameChange}
                                    />
                                    <FieldErrorList
                                        field={SubCategoryField.Name}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Sort Order (required)"></Label>
                                    <Input
                                        type={InputType.TEXT}
                                        value={sortOrder}
                                        onChange={handleSortOrderChange}
                                    />
                                    <FieldErrorList
                                        field={SubCategoryField.SortOrder}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Description"></Label>
                                    <TextArea
                                        className="subcategory-form-textarea"
                                        value={description}
                                        onChange={handleDescription}
                                    />
                                    <FieldErrorList
                                        field={SubCategoryField.Description}
                                        errors={fieldErrors}
                                        data-testid="error"
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button size="medium" type="button" onClick={handeSave}>
                        {ACTION_ITEMS.SUBMIT_BUTTON}
                    </Button>
                    <Button size="medium" variant="secondary" type="button" onClick={onClose}>
                        {ACTION_ITEMS.CANCEL_BUTTON}
                    </Button>
                </ModalFooter>
            </Modal>

            {isConfirmationVisible && (
                <MessageModal
                    headerName={CLONE_CONFIRMATION_TITLE}
                    message={CLONE_CONFIRMATION_DESCRIPTION}
                    isVisible={isConfirmationVisible}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfirmation}
                    onSecondaryAction={handleConfirmationClose}
                />
            )}

            {isChooseCloneErrorInfo && (
                <MessageModal
                    headerName={CLONE_SUB_CATEGORY_TITLE}
                    message={CLONE_VALIDATION_ERROR_MESSAGE}
                    isVisible={isChooseCloneErrorInfo}
                    type={MessageType.Error}
                    isSecondaryButtonVisible={false}
                    primaryButton={ButtonLabel.Ok}
                    onPrimaryAction={() => {
                        setChooseCloneErrorInfo(false);
                    }}
                    onSecondaryAction={() => {
                        setChooseCloneErrorInfo(false);
                    }}
                />
            )}
        </>
    );
}
